import { AnyAction } from 'redux'
import { ThunkDispatch } from 'redux-thunk'
import { startLoading, stopLoading } from '../loading/actions'
import { LoadingTypes } from '../loading/reducer'
import { LABRA_CHAT_LOAD_SESSION } from './loadingTypes'
import { updateAppAlert } from '../appAlert/actions'
import { errorLogger } from '../../utils/errorLogger'
import { createSSEConnection } from '../../utils/sseHelper'
import {
  AgentStage,
  LabraChatLayout,
  LabraChatList,
  CompletedStage,
  FundingProgressStep,
  FundingStepStatus,
  LabraChatActionTypes,
  LabraChatEmailPreview,
  LabraChatMessage,
  LabraChatMessageDocument,
  LabraChatScheduleDetails,
  LabraChatState,
  NextBestAction,
} from './reducer'
import { UserProfileState } from '../userProfile/reducer'
import { v4 as uuidv4 } from 'uuid'
import { buildChatStreamRequest, ChatUploadedDocument } from './api'
import { getChatRequestContext } from './chatRequestContext'
import { createTypewriter } from '../../utils/streamTypewriter'
import { ChatHistoryMessage, getChatHistoryAPI } from './historyApi'
import {
  deleteChatPromptAPI,
  getChatPromptsAPI,
  saveChatPromptAPI,
} from './promptsApi'
import { emitProductEvent } from '../../telemetry/productEvents'
import { endChatTurn, startChatTurn } from './telemetry'

/**
 * Minimal store shape the chat thunks read. Any host store that mounts the
 * common reducers (or the funding standalone harness) satisfies this
 * structurally — it stays a superset-tolerant shape via the index signature so
 * it also works as `errorLogger`'s `globalState`.
 */
export interface LabraChatThunkState {
  labraChat?: LabraChatState
  userProfile?: UserProfileState
  [key: string]: unknown
}

export type LabraChatThunkDispatch = ThunkDispatch<
  LabraChatThunkState,
  unknown,
  AnyAction
>

/** Max wait per SSE read before the stream is considered dead (5 min). */
const STREAM_TIMEOUT_MS = 300000
/** Number of connection attempts (1 = no retry; the helper loops `< attempts`). */
const STREAM_ATTEMPTS = 1

let currentSSECancel: (() => void) | null = null
let currentTypewriterStop: (() => void) | null = null
// Monotonic id of the most recent send. A superseded send (e.g. the user fired
// another CTA mid-stream) must not clear the loading flag the newer send owns.
let latestSendSeq = 0

const cancelLabraChatSSE = () => {
  if (currentSSECancel) {
    currentSSECancel()
    currentSSECancel = null
  }
  if (currentTypewriterStop) {
    currentTypewriterStop()
    currentTypewriterStop = null
  }
}

export const addLabraChatMessage = (message: LabraChatMessage) => ({
  type: LabraChatActionTypes.ADD_MESSAGE as const,
  payload: message,
})

export const updateLabraChatMessage = (
  id: string,
  updates: Partial<LabraChatMessage>
) => ({
  type: LabraChatActionTypes.UPDATE_MESSAGE as const,
  payload: { id, updates },
})

export const setLabraChatMessages = (messages: LabraChatMessage[]) => ({
  type: LabraChatActionTypes.SET_MESSAGES as const,
  payload: messages,
})

export const clearLabraChatMessages = () => ({
  type: LabraChatActionTypes.CLEAR_MESSAGES as const,
})

export const setLabraChatSessionId = (sessionId: string) => ({
  type: LabraChatActionTypes.SET_SESSION_ID as const,
  payload: sessionId,
})

export const setLabraChatAgentType = (agentType: string) => ({
  type: LabraChatActionTypes.SET_AGENT_TYPE as const,
  payload: agentType,
})

/** Insert text into the composer for the user to edit (sample prompts). */
export const setLabraChatComposerDraft = (draft: string | null) => ({
  type: LabraChatActionTypes.SET_COMPOSER_DRAFT as const,
  payload: draft,
})

/**
 * Thunks rather than plain creators so the open/close telemetry fires when the
 * panel actually changes state — re-opening an already-open panel is not a new
 * chat session, and would otherwise double-count.
 */
export const openLabraChat =
  () =>
  (dispatch: LabraChatThunkDispatch, getState: () => LabraChatThunkState) => {
    if (!getState().labraChat?.isPanelOpen) {
      emitProductEvent({ name: 'labra_ai_chat_opened' })
    }
    dispatch({ type: LabraChatActionTypes.OPEN_PANEL as const })
  }

export const closeLabraChat =
  () =>
  (dispatch: LabraChatThunkDispatch, getState: () => LabraChatThunkState) => {
    if (getState().labraChat?.isPanelOpen) {
      emitProductEvent({ name: 'labra_ai_chat_closed' })
    }
    dispatch({ type: LabraChatActionTypes.CLOSE_PANEL as const })
  }

/**
 * Fetch the user's saved prompts once per session of the panel being open, so
 * opening the list is instant and does not flash a loading state. Failure is
 * silent: an empty list reads the same as "nothing saved yet", and losing this
 * must not disturb the conversation.
 */
export const loadLabraChatSavedPrompts =
  (partnerId: string) =>
  async (
    dispatch: LabraChatThunkDispatch,
    getState: () => LabraChatThunkState
  ) => {
    const state = getState()
    if (state.labraChat?.areSavedPromptsLoaded) return

    const userId = state.userProfile?.userProfile?.id || ''
    if (!partnerId || !userId) return

    try {
      const { data } = await getChatPromptsAPI(partnerId, userId)
      dispatch({
        type: LabraChatActionTypes.SET_SAVED_PROMPTS as const,
        payload: data.prompts,
      })
    } catch {
      // Leave the list unloaded so the next open retries.
    }
  }

/**
 * The backend stores one prompt per partner + user + text, so saving the same
 * text again answers 200 with the row that already exists rather than 201 with
 * a new one.
 */
export type SavePromptResult = 'created' | 'existing' | 'failed'

/** Store one prompt and fold the response into the list. */
export const saveLabraChatPrompt =
  (partnerId: string, prompt: string) =>
  async (
    dispatch: LabraChatThunkDispatch,
    getState: () => LabraChatThunkState
  ): Promise<SavePromptResult> => {
    const userId = getState().userProfile?.userProfile?.id || ''
    if (!partnerId || !userId || !prompt) return 'failed'

    try {
      const { data, status } = await saveChatPromptAPI(
        partnerId,
        userId,
        prompt
      )
      dispatch({
        type: LabraChatActionTypes.UPSERT_SAVED_PROMPT as const,
        payload: data,
      })
      return status === 201 ? 'created' : 'existing'
    } catch {
      return 'failed'
    }
  }

/** Drop a saved prompt. Removed locally first — a 404 also means it is gone. */
export const deleteLabraChatSavedPrompt =
  (partnerId: string, promptId: string) =>
  async (
    dispatch: LabraChatThunkDispatch,
    getState: () => LabraChatThunkState
  ) => {
    const userId = getState().userProfile?.userProfile?.id || ''
    dispatch({
      type: LabraChatActionTypes.REMOVE_SAVED_PROMPT as const,
      payload: promptId,
    })
    if (!partnerId || !userId) return
    await deleteChatPromptAPI(partnerId, userId, promptId).catch(
      () => undefined
    )
  }

export const setLabraChatLayout = (layout: LabraChatLayout) => ({
  type: LabraChatActionTypes.SET_LAYOUT as const,
  payload: layout,
})

/** Show one of the welcome's lists in place of the conversation, or null. */
export const setLabraChatOpenList = (list: LabraChatList | null) => ({
  type: LabraChatActionTypes.SET_OPEN_LIST as const,
  payload: list,
})

/** Remember the list a conversation was started from, so back returns there. */
export const setLabraChatListOrigin = (list: LabraChatList | null) => ({
  type: LabraChatActionTypes.SET_LIST_ORIGIN as const,
  payload: list,
})

/** Follow-up action buttons surfaced by the agent (`next_best_actions`). */
export const setLabraChatNextBestActions = (actions: NextBestAction[]) => ({
  type: LabraChatActionTypes.SET_NEXT_BEST_ACTIONS as const,
  payload: actions,
})

/** Whether the agent is currently computing its follow-up actions (shown in the
 * suggestion-chip slot as "Generating next best actions for you"). */
export const setLabraChatGeneratingNextActions = (value: boolean) => ({
  type: LabraChatActionTypes.SET_GENERATING_NEXT_ACTIONS as const,
  payload: value,
})

/** Stop any still-streaming assistant message from being stuck mid-stream. */
const finalizeStreamingLabraChatMessages = () => ({
  type: LabraChatActionTypes.FINALIZE_STREAMING as const,
})

/**
 * User-triggered "stop generating": abort the in-flight SSE + typewriter,
 * finalize the partially-streamed bubble (keeping whatever text was revealed),
 * and clear the loading flags. The typewriter is terminal once stopped, so its
 * onComplete never fires — hence we finalize the message explicitly here.
 */
export const stopLabraChatMessage =
  () => (dispatch: LabraChatThunkDispatch) => {
    endChatTurn('cancelled')
    cancelLabraChatSSE()
    dispatch(finalizeStreamingLabraChatMessages())
    dispatch(stopLoading(LoadingTypes.GENERATE_AI_AGENT_LOADING))
    dispatch({ type: LabraChatActionTypes.SET_LOADING, payload: false })
  }

export const toggleLabraChatFullScreen = () => ({
  type: LabraChatActionTypes.TOGGLE_FULLSCREEN as const,
})

/** Open an attachment beside the conversation. Forces full screen, the only
 * layout wide enough for two columns. */
export const openLabraChatPreview = (document: LabraChatMessageDocument) => ({
  type: LabraChatActionTypes.OPEN_PREVIEW as const,
  payload: document,
})

export const closeLabraChatPreview = () => ({
  type: LabraChatActionTypes.CLOSE_PREVIEW as const,
})

/** Open the email HTML preview as a right split pane (Figma 641:4730). */
export const openLabraChatEmailPreview = (
  preview: LabraChatEmailPreview
) => ({
  type: LabraChatActionTypes.OPEN_EMAIL_PREVIEW as const,
  payload: preview,
})

export const closeLabraChatEmailPreview = () => ({
  type: LabraChatActionTypes.CLOSE_EMAIL_PREVIEW as const,
})

/** Show the chat-history list in place of the active conversation. */
export const openLabraChatHistory = () => ({
  type: LabraChatActionTypes.OPEN_HISTORY as const,
})

/** Return from the history list to the conversation view. */
export const closeLabraChatHistory = () => ({
  type: LabraChatActionTypes.CLOSE_HISTORY as const,
})

/** The user closed the page-context pill: send no context for this session. */
export const dismissLabraChatPageContext = () => ({
  type: LabraChatActionTypes.DISMISS_PAGE_CONTEXT as const,
})

export const markLabraChatPageContextSent = () => ({
  type: LabraChatActionTypes.SET_PAGE_CONTEXT_SENT as const,
})

export const resetLabraChatPageContext = () => ({
  type: LabraChatActionTypes.RESET_PAGE_CONTEXT as const,
})

/**
 * Reset to a fresh, empty conversation: drop the messages, the session id, and
 * the previous turn's follow-up actions (so the chip slot falls back to the
 * starter prompts), and leave the history list if it was open. Shared by the
 * header's "New chat" control and the history view's "New chat" button.
 */
export const startNewLabraChat = () => (dispatch: LabraChatThunkDispatch) => {
  // Tear down any in-flight stream first (same cancel/finalize path as loading
  // another session) so its tokens can't bleed into the fresh chat. The
  // cancelled send's own `finally` balances the loading flags.
  endChatTurn('navigated')
  cancelLabraChatSSE()
  dispatch(finalizeStreamingLabraChatMessages())
  dispatch(clearLabraChatMessages())
  dispatch(setLabraChatSessionId(''))
  dispatch(setLabraChatNextBestActions([]))
  dispatch(setLabraChatGeneratingNextActions(false))
  dispatch(closeLabraChatHistory())
  // A new conversation gets a fresh shot at page context, including if the user
  // dismissed the pill in the previous one.
  dispatch(resetLabraChatPageContext())
}

/** Friendly labels for the agent's tools (from `graph_event` `name`). */
const TOOL_LABELS: Record<string, string> = {
  list_runs: 'Checking funding runs',
  get_run: 'Fetching run details',
  resume_run: 'Submitting funding application',
  start_run: 'Starting funding workflow',
  draft_email: 'Drafting email preview',
  send_drafted_email: 'Sending email',
  create_ai_job: 'Creating AI schedule',
  update_ai_job: 'Updating AI schedule',
}

const toolLabel = (name?: string): string => {
  if (!name) return 'Using tools'
  return TOOL_LABELS[name] ?? `Using ${name.replace(/_/g, ' ')}`
}

/**
 * Pull the OpenUI-Lang program out of a `render_ui` tool result. The tool's
 * `on_tool_end` output is a `ToolMessage` whose `content` is a JSON string
 * `{ content_type: "openui_lang", lang: "root = …" }`. Returns the `lang` source
 * or null for any other tool / shape, so the caller renders generative UI only
 * when there genuinely is one.
 */
export const extractRenderUiLang = (payload: {
  data?: { output?: { content?: unknown } }
}): string | null => {
  const content = payload?.data?.output?.content
  if (typeof content !== 'string') return null
  try {
    const parsed = JSON.parse(content) as {
      content_type?: string
      lang?: unknown
    }
    return parsed?.content_type === 'openui_lang' &&
      typeof parsed.lang === 'string'
      ? parsed.lang
      : null
  } catch {
    return null
  }
}

/** Parse a tool result JSON object from on_tool_end payload content. */
const parseToolResultJson = (payload: {
  data?: { output?: { content?: unknown } }
}): Record<string, unknown> | null => {
  const content = payload?.data?.output?.content
  if (typeof content !== 'string') return null
  try {
    const parsed = JSON.parse(content) as unknown
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : null
  } catch {
    return null
  }
}

/**
 * Pull an HTML email draft out of a `draft_email` tool result
 * (`content_type: "email_html"`).
 */
export const extractEmailHtml = (payload: {
  data?: { output?: { content?: unknown } }
}): LabraChatEmailPreview | null => {
  const parsed = parseToolResultJson(payload)
  if (!parsed || parsed.content_type !== 'email_html') return null
  const draftId = parsed.draft_id
  const subject = parsed.subject
  const html = parsed.html
  if (
    typeof draftId !== 'string' ||
    !draftId ||
    typeof subject !== 'string' ||
    typeof html !== 'string' ||
    !html
  ) {
    return null
  }
  const recipient =
    typeof parsed.recipient_email === 'string' ? parsed.recipient_email : null
  return {
    draftId,
    subject,
    html,
    recipientEmail: recipient,
  }
}

/**
 * Pull Schedule Details fields from a tool result
 * (`content_type: "schedule_details"`).
 */
export const extractScheduleDetails = (payload: {
  data?: { output?: { content?: unknown } }
}): LabraChatScheduleDetails | null => {
  const parsed = parseToolResultJson(payload)
  if (!parsed || parsed.content_type !== 'schedule_details') return null
  const reportName =
    typeof parsed.report_name === 'string'
      ? parsed.report_name
      : typeof parsed.name === 'string'
        ? parsed.name
        : ''
  const frequency =
    typeof parsed.frequency === 'string'
      ? parsed.frequency
      : typeof parsed.frequency_expression === 'string'
        ? parsed.frequency_expression
        : ''
  const timezone =
    typeof parsed.timezone === 'string' ? parsed.timezone : 'UTC'
  const delivery =
    typeof parsed.delivery === 'string'
      ? parsed.delivery
      : typeof parsed.receive_via === 'string'
        ? parsed.receive_via
        : 'Email'
  const startsOn =
    typeof parsed.starts_on === 'string'
      ? parsed.starts_on
      : typeof parsed.startsOn === 'string'
        ? parsed.startsOn
        : undefined
  let recipients: string[] = []
  if (Array.isArray(parsed.recipients)) {
    recipients = parsed.recipients.map(String).filter(Boolean)
  } else if (typeof parsed.email_recipient === 'string' && parsed.email_recipient) {
    recipients = [parsed.email_recipient]
  }
  if (!reportName && !frequency) return null
  return {
    reportName: reportName || 'Untitled schedule',
    frequency: frequency || '—',
    timezone,
    recipients,
    delivery,
    ...(startsOn ? { startsOn } : {}),
  }
}

/**
 * `progress` stage keys carrying funding-run progress. These ride the same
 * `kind:'status'`, `message:'progress'` frames as the generic agent stages but
 * are handled separately (they drive the determinate stepper, not the spinner).
 */
const FUNDING_STAGE = {
  plan: 'funding_plan',
  step: 'funding_step',
} as const

/** POC resume submits directly without replaying stages, so the stepper can't
 * fill in — show an indeterminate "Submitting…" state for this tool. */
const INDETERMINATE_FUNDING_TOOL = 'resume_poc_run'

/** Maps a `funding_step.funding_status` value to a step's UI status. */
const toFundingStepStatus = (status?: string): FundingStepStatus => {
  switch (status) {
    case 'ok':
      return 'done'
    case 'failed':
    case 'error':
      return 'failed'
    case 'running':
    default:
      return 'running'
  }
}

/** Maps a v2 stream `progress` stage key to a UI agent stage + label. */
const mapStage = (stage?: string): { stage: AgentStage; message: string } => {
  switch (stage) {
    case 'loading_context':
      return { stage: 'retrieving', message: 'Loading context' }
    case 'loading_memory':
      return { stage: 'retrieving', message: 'Recalling memory' }
    case 'model':
    case 'invoking_model':
    case 'generating':
      return { stage: 'thinking', message: 'Thinking' }
    case 'invoking_tool':
    case 'tool_use':
      return { stage: 'using_tool', message: 'Using tools' }
    case 'processing_tool_result':
      return { stage: 'processing', message: 'Processing results' }
    default:
      return {
        stage: 'processing',
        message: stage
          ? `${stage.charAt(0).toUpperCase()}${stage.slice(1)}`.replace(
              /_/g,
              ' '
            )
          : 'Working',
      }
  }
}

export const sendLabraChatMessage =
  (
    partnerId: string,
    params: {
      userId: string
      sessionId: string
      /** Id of the turn this send opened, for its response telemetry. */
      turnId: string
      message: string
      documents?: ChatUploadedDocument[]
    }
  ) =>
  async (
    dispatch: LabraChatThunkDispatch,
    getState: () => LabraChatThunkState
  ) => {
    // Claim ownership of the loading flag for this send.
    const sendSeq = ++latestSendSeq
    await dispatch(startLoading(LoadingTypes.GENERATE_AI_AGENT_LOADING))
    dispatch({ type: LabraChatActionTypes.SET_LOADING, payload: true })
    // Drop any prior follow-up CTAs; the new response may emit fresh ones.
    dispatch(setLabraChatNextBestActions([]))
    dispatch(setLabraChatGeneratingNextActions(false))

    let loaderStopped = false
    let errorSurfaced = false

    // This thunk's own cancel handles. The module-level singletons may be
    // reassigned by a newer send, so we only ever clear them if they still
    // point at THIS thunk's handles (identity guard) — preventing a resumed
    // older thunk from nulling out a newer one's cancel handle.
    let ownCancel: (() => void) | null = null
    let ownTypewriterStop: (() => void) | null = null
    const releaseOwnedHandles = () => {
      if (ownCancel && currentSSECancel === ownCancel) currentSSECancel = null
      if (ownTypewriterStop && currentTypewriterStop === ownTypewriterStop) {
        currentTypewriterStop = null
      }
    }

    // Show the error toast at most once, even though both the SSE onError
    // callback and the outer catch can fire for a single failure.
    const surfaceError = (message: string) => {
      if (errorSurfaced) return
      errorSurfaced = true
      dispatch(
        updateAppAlert({ message, messageType: 'ERROR', autoClose: true })
      )
    }

    const stopLoaders = () => {
      if (loaderStopped) return
      loaderStopped = true
      // Always balance this send's startLoading (it's reference-counted).
      dispatch(stopLoading(LoadingTypes.GENERATE_AI_AGENT_LOADING))
      // Only the latest send owns the button's loading flag — a superseded
      // send tearing down must not flip the stop button back to "send".
      if (sendSeq === latestSendSeq) {
        dispatch({ type: LabraChatActionTypes.SET_LOADING, payload: false })
      }
    }

    try {
      // Abort any in-flight stream and finalize its (now-orphaned) message so
      // the previous bubble doesn't stay stuck on its thinking spinner.
      cancelLabraChatSSE()
      dispatch(finalizeStreamingLabraChatMessages())

      const chat = getState().labraChat
      // Read at send time so the context matches the page the user is on now,
      // not whichever page was open when the thunk was created.
      const pageContext =
        chat?.isPageContextDismissed || chat?.isPageContextSent
          ? null
          : getChatRequestContext()

      const { url, body } = buildChatStreamRequest(partnerId, {
        user_id: params.userId,
        session_id: params.sessionId,
        message: params.message,
        agent_type: getState().labraChat?.agentType,
        documents: params.documents,
        // One-shot, and only with the user's consent: the pill lets them drop it,
        // and after the first message the agent already knows where we started.
        context: pageContext,
      })

      // Consume the one-shot regardless of whether context was actually sent:
      // the first message of the conversation has now gone out, so the pill must
      // not keep offering a page the agent will never be told about.
      dispatch(markLabraChatPageContextSent())

      let assistantMessageId: string | null = null
      let isStreamComplete = false
      let responseStarted = false
      let completedStages: CompletedStage[] = []
      let currentStage: { stage: AgentStage; message: string } | null = null

      // The tool currently running (shown as a spinner row). Tracked locally so
      // we only dispatch when it actually changes.
      let activeToolLabel: string | null = null
      const setActiveTool = (label: string | null) => {
        if (activeToolLabel === label) return
        activeToolLabel = label
        if (assistantMessageId) {
          dispatch(
            updateLabraChatMessage(assistantMessageId, { activeTool: label })
          )
        }
      }

      // Determinate funding-run progress (stepper card). Tracked locally so we
      // can advance individual steps without round-tripping through the store,
      // mirroring how `completedStages`/`activeToolLabel` are kept. Null until a
      // `funding_plan` frame arrives.
      let fundingTool: string | null = null
      let fundingProgram = ''
      let fundingOpportunity = ''
      let fundingIndeterminate = false
      let fundingSteps: FundingProgressStep[] = []
      const dispatchFundingProgress = (ended = false) => {
        if (!assistantMessageId || !fundingTool) return
        dispatch(
          updateLabraChatMessage(assistantMessageId, {
            fundingProgress: {
              tool: fundingTool,
              program: fundingProgram,
              ...(fundingOpportunity
                ? { opportunityName: fundingOpportunity }
                : {}),
              steps: fundingSteps,
              indeterminate: fundingIndeterminate,
              ended,
            },
          })
        )
      }
      // Settle a step only when the workflow published it and it was still
      // running. Pending steps never started, and a failed step stays failed,
      // so a rejected create cannot read as submitted.
      const endFundingProgress = () => {
        if (!fundingTool) return
        const anyFailed = fundingSteps.some(s => s.status === 'failed')
        fundingSteps = fundingSteps.map(s => {
          if (s.status !== 'running') return s
          return { ...s, status: anyFailed ? 'failed' : 'done' }
        })
        dispatchFundingProgress(true)
      }

      // Build the stepper skeleton from a `funding_plan` frame (emitted once).
      const startFundingPlan = (payload: Record<string, any>) => {
        fundingTool = payload.tool ?? 'funding_run'
        fundingProgram = payload.program ?? ''
        fundingOpportunity =
          payload.opportunity_name ?? payload.opportunity_id ?? ''
        fundingIndeterminate = fundingTool === INDETERMINATE_FUNDING_TOOL
        fundingSteps = Array.isArray(payload.steps)
          ? payload.steps.map((s: any) => ({
              step: s.step,
              label: s.label,
              status: 'pending' as FundingStepStatus,
            }))
          : []
        dispatchFundingProgress()
      }

      // Advance the stepper from a `funding_step` frame. Reaching a later step
      // closes an earlier step only when that step was actually running. A
      // pending step was never published, so it stays pending.
      const advanceFundingStep = (payload: Record<string, any>) => {
        const stepName = String(payload.funding_step || payload.step || '')
        if (!stepName) return
        let idx = fundingSteps.findIndex(s => s.step === stepName)
        if (idx < 0) {
          fundingSteps = [
            ...fundingSteps,
            {
              step: stepName,
              label:
                typeof payload.label === 'string' && payload.label
                  ? payload.label
                  : stepName,
              status: 'pending' as FundingStepStatus,
            },
          ]
          idx = fundingSteps.length - 1
        }
        const status = toFundingStepStatus(
          payload.funding_status || payload.status
        )
        fundingSteps = fundingSteps.map((s, i) => {
          if (i < idx && s.status === 'running') {
            return { ...s, status: 'done' }
          }
          if (i === idx) {
            return {
              ...s,
              status,
              detail:
                typeof payload.detail === 'string' ? payload.detail : s.detail,
            }
          }
          return s
        })
        dispatchFundingProgress()
      }

      // "Generating next best actions" affordance — shown in the suggestion-chip
      // slot when the answer has streamed and we're idling on the trailing gap
      // (no tool running) while the agent computes its follow-up actions.
      // State-level (not per-message) so SuggestionChips can read it. Tracked
      // locally to dispatch only on change.
      let generatingActions = false
      const setGeneratingActions = (value: boolean) => {
        if (generatingActions === value) return
        generatingActions = value
        dispatch(setLabraChatGeneratingNextActions(value))
      }

      // --- Follow-up (next_best_actions) reconciliation -----------------------
      // The bottom chip slot must follow the TYPEWRITER, not raw event receipt:
      // the backend delivers the whole turn (tokens → nba_start →
      // next_best_actions → done) almost instantly, while the typewriter animates
      // the answer over seconds. So we hold the post-answer state and apply it
      // only once the answer has finished animating.
      //   - nbaStarted:     the agent began computing follow-up actions
      //   - pendingActions:  follow-up actions received, not yet shown
      //   - answerRevealed:  the typewriter has fully revealed the answer
      // reconcile() is the single source of truth for the chip slot.
      let nbaStarted = false
      let pendingActions: NextBestAction[] | null = null
      let answerRevealed = false
      const reconcileFollowUps = () => {
        // Don't let a superseded send write, and wait until the answer is shown.
        if (sendSeq !== latestSendSeq || !answerRevealed) return
        if (pendingActions) {
          // Actions are ready and the answer is fully shown → render the chips.
          dispatch(setLabraChatNextBestActions(pendingActions))
          pendingActions = null
          nbaStarted = false
          setGeneratingActions(false)
          return
        }
        // No actions yet: show "Generating…" iff the agent said it's working on
        // them (nba_start), otherwise nothing.
        setGeneratingActions(nbaStarted)
      }

      const recordCurrentStageAsComplete = () => {
        if (!currentStage) return
        // Never list the same label twice in a row.
        const last = completedStages[completedStages.length - 1]
        if (last && last.message === currentStage.message) return
        completedStages = [
          ...completedStages,
          {
            stage: currentStage.stage,
            message: currentStage.message,
            timestamp: new Date().toISOString(),
          },
        ]
      }

      const advanceStage = (next: { stage: AgentStage; message: string }) => {
        if (currentStage && currentStage.message !== next.message) {
          recordCurrentStageAsComplete()
        }
        currentStage = next
      }

      const finishStages = () => {
        recordCurrentStageAsComplete()
        currentStage = null
      }

      const ensureAssistantMessage = () => {
        if (assistantMessageId) return
        assistantMessageId = uuidv4()
        dispatch(
          addLabraChatMessage({
            id: assistantMessageId,
            role: 'ASSISTANT',
            content: '',
            isStreaming: true,
            agentStage: 'thinking',
            // Distinct from any mapped stage label so the same word never
            // shows at start and again mid-run (e.g. the 'model' → "Thinking").
            stageMessage: 'Getting started',
            completedStages: [],
            timestamp: new Date().toISOString(),
          })
        )
      }

      // Mark the bubble done. Pass `finalText` to overwrite content, or omit to
      // keep whatever the typewriter last revealed (error / cancel paths).
      const finalizeMessage = (finalText?: string) => {
        if (!assistantMessageId || isStreamComplete) return
        isStreamComplete = true
        releaseOwnedHandles()
        // Freeze any funding stepper at its last state on every terminal path
        // (done, error, cancel, network close).
        endFundingProgress()
        dispatch(
          updateLabraChatMessage(assistantMessageId, {
            ...(finalText !== undefined ? { content: finalText } : {}),
            isStreaming: false,
            agentStage: 'complete',
            stageMessage: null,
            activeTool: null,
          })
        )
        setGeneratingActions(false)
        stopLoaders()
        // No-ops when an error path already closed the turn as interrupted.
        endChatTurn()
      }

      // Smoothly drip-feed streamed text to the bubble (see streamTypewriter).
      const typewriter = createTypewriter({
        onReveal: visibleText => {
          // More text is flowing — the answer isn't fully shown yet.
          answerRevealed = false
          if (!assistantMessageId) return
          dispatch(
            updateLabraChatMessage(assistantMessageId, {
              content: visibleText,
              agentStage: 'responding',
              stageMessage: null,
              completedStages,
              // New text is flowing — not settled, so keep the cheap plain-text
              // reveal (markdown re-render happens once it settles, see onIdle).
              streamSettled: false,
            })
          )
        },
        // Reveal caught up and held steady (e.g. during the gap before the late
        // `done`): render the full markdown now instead of waiting for `done`.
        // The "generating next best actions" affordance is driven by the
        // backend `nba_start` event (see onData), not by this token-pause.
        onIdle: () => {
          // The reveal caught up with all received text — the answer is shown.
          answerRevealed = true
          if (assistantMessageId) {
            dispatch(
              updateLabraChatMessage(assistantMessageId, {
                streamSettled: true,
              })
            )
          }
          reconcileFollowUps()
        },
        onComplete: finalText => {
          answerRevealed = true
          finalizeMessage(finalText)
          reconcileFollowUps()
        },
      })
      ownTypewriterStop = typewriter.stop
      currentTypewriterStop = typewriter.stop

      const connection = createSSEConnection(
        { url, body },
        {
          // The v2 "labra" stream emits { kind, data } events; the generic SSE
          // normalizer can't type them, so every event lands in onData with the
          // raw payload preserved on event.data.
          onData: event => {
            const raw = event.data as
              | { kind?: string; data?: Record<string, any> }
              | undefined
            const kind = raw?.kind
            const payload = raw?.data ?? {}
            if (!kind) return

            if (kind === 'status') {
              if (payload.message === 'started') {
                if (payload.session_id) {
                  dispatch(setLabraChatSessionId(payload.session_id))
                }
                ensureAssistantMessage()
              } else if (payload.message === 'progress') {
                ensureAssistantMessage()
                // Funding-run frames drive the determinate stepper, not the
                // generic stage spinner — handle and skip the stage advance.
                if (payload.stage === FUNDING_STAGE.plan) {
                  startFundingPlan(payload)
                  return
                }
                if (payload.stage === FUNDING_STAGE.step) {
                  advanceFundingStep(payload)
                  return
                }
                const mapped = mapStage(payload.stage)
                advanceStage(mapped)
                if (assistantMessageId) {
                  dispatch(
                    updateLabraChatMessage(assistantMessageId, {
                      agentStage: mapped.stage,
                      stageMessage: mapped.message,
                      completedStages,
                    })
                  )
                }
              }
            } else if (kind === 'graph_event') {
              // LangGraph telemetry. Surface tool execution so a tool call isn't
              // a silent pause: hold a spinner from on_tool_start until the
              // answer resumes (the next token clears it).
              const graphEvent = payload.event
              if (graphEvent === 'on_tool_start') {
                ensureAssistantMessage()
                setActiveTool(toolLabel(payload.name))
              } else if (graphEvent === 'on_tool_end') {
                // A `render_ui` tool returns OpenUI-Lang to render as generative
                // UI; attach it to the turn's message (see AssistantBubble).
                const lang = extractRenderUiLang(payload)
                const emailPreview = extractEmailHtml(payload)
                const scheduleDetails = extractScheduleDetails(payload)
                if (assistantMessageId && (lang || emailPreview || scheduleDetails)) {
                  dispatch(
                    updateLabraChatMessage(assistantMessageId, {
                      ...(lang ? { openUiLang: lang } : {}),
                      ...(emailPreview ? { emailPreview } : {}),
                      ...(scheduleDetails ? { scheduleDetails } : {}),
                    })
                  )
                  // After a draft, open the HTML preview modal and offer chips.
                  if (emailPreview) {
                    dispatch(openLabraChatEmailPreview(emailPreview))
                    const sendPrompt = emailPreview.recipientEmail
                      ? `Yes, send the preview email to ${emailPreview.recipientEmail} (draft_id: ${emailPreview.draftId})`
                      : `Yes, send the preview email (draft_id: ${emailPreview.draftId})`
                    dispatch(
                      setLabraChatNextBestActions([
                        {
                          label: 'Preview',
                          prompt: `__labra_email_preview__:${emailPreview.draftId}`,
                        },
                        {
                          label: 'Yes, send it',
                          prompt: sendPrompt,
                        },
                      ])
                    )
                    dispatch(setLabraChatGeneratingNextActions(false))
                  }
                  // The rendered visual IS the result — don't leave a lingering
                  // "Processing results" label under it while the model (often
                  // slowly) composes the accompanying prose.
                  setActiveTool(null)
                } else {
                  setActiveTool('Processing results')
                }
                // Freeze the stepper only when the funding tool itself ends —
                // not some other tool the agent ran in the same turn, which
                // would otherwise flicker the card to its frozen/complete state
                // until the next funding frame revived it. The turn's `done` /
                // finalize paths still freeze it as a backstop.
                if (payload.name && payload.name === fundingTool) {
                  endFundingProgress()
                }
              }
            } else if (kind === 'token') {
              ensureAssistantMessage()
              finishStages()
              setActiveTool(null)
              if (!responseStarted) {
                responseStarted = true
                emitProductEvent({
                  name: 'labra_ai_chat_response_started',
                  conversation_id: params.sessionId,
                  turn_id: params.turnId,
                })
              }
              typewriter.append(payload.text ?? '')
            } else if (kind === 'done') {
              ensureAssistantMessage()
              finishStages()
              setActiveTool(null)
              endFundingProgress()
              // next_best_actions is sent only when non-empty, so if none has
              // arrived by `done` there are no follow-ups — drop the pending
              // "generating" state. reconcile() runs on typewriter completion.
              if (!pendingActions) nbaStarted = false
              if (typeof payload.text === 'string') {
                typewriter.setTarget(payload.text)
              }
              typewriter.finish()
            } else if (kind === 'nba_start') {
              // The agent has begun computing its follow-up actions. The chip
              // slot only updates once the answer has finished animating, so
              // this never flashes mid-answer (see reconcileFollowUps).
              ensureAssistantMessage()
              nbaStarted = true
              reconcileFollowUps()
            } else if (kind === 'next_best_actions') {
              // Follow-up CTAs the agent suggests after a response. Held until
              // the answer is fully revealed, then rendered as chips.
              const parsed: NextBestAction[] = Array.isArray(payload.actions)
                ? payload.actions
                    .filter(
                      (a: any) => a && typeof a.prompt === 'string' && a.label
                    )
                    .map((a: any) => ({ label: a.label, prompt: a.prompt }))
                : []
              // Keep null (never []) so reconcile's truthy check is unambiguous;
              // an empty payload just means "no follow-ups".
              pendingActions = parsed.length ? parsed : null
              if (!pendingActions) nbaStarted = false
              reconcileFollowUps()
            } else if (kind === 'error') {
              surfaceError(payload.message || 'Labra AI ran into an error')
              typewriter.stop()
              endChatTurn('error')
              finalizeMessage()
            }
          },
          onError: event => {
            typewriter.stop()
            releaseOwnedHandles()
            surfaceError(event.message || 'Failed to send message to Labra AI')
            endChatTurn('error')
            if (assistantMessageId) finalizeMessage()
            else stopLoaders()
          },
        },
        { timeout: STREAM_TIMEOUT_MS, retryAttempts: STREAM_ATTEMPTS }
      )

      ownCancel = connection.cancel
      currentSSECancel = connection.cancel
      await connection.promise
      releaseOwnedHandles()

      // Network stream ended (no explicit "done"). Let the typewriter drain any
      // buffered text and then finalize; if nothing streamed, finalize now.
      // (If this stream was superseded, typewriter is already cancelled and
      // finish() is a no-op, so we can't resurrect a finalized bubble.)
      if (!isStreamComplete) {
        if (assistantMessageId) {
          typewriter.finish()
        } else {
          finalizeMessage('')
        }
      }
    } catch (error: any) {
      if (ownTypewriterStop) ownTypewriterStop()
      releaseOwnedHandles()
      console.error('LabraChat SSE error:', error)
      surfaceError(error.message || 'Failed to send message to Labra AI')
      endChatTurn('error')
      // Never leave the bubble stuck on its spinner if we landed here.
      dispatch(finalizeStreamingLabraChatMessages())
      const globalState = getState()
      errorLogger({ globalState })(error as Error)
    } finally {
      stopLoaders()
    }
  }

/**
 * Enhanced prompt fired by the "Recent activities" quick-action button. It asks
 * the agent for a tightly-scoped, ready-to-render markdown table so the reply
 * drops straight into the thread with no preamble to strip. Columns are pinned
 * to Company / Amount / Status / Note in that exact order.
 *
 * NOTE: kept under 255 characters — the chat backend derives the conversation
 * title from the first message and rejects (HTTP 422) titles longer than that.
 */
export const RECENT_ACTIVITY_PROMPT =
  'Show my recent activity as a markdown table, no preamble. Columns: ' +
  'Opp ID, Company, Amount, Status, Note. Status = current stage (e.g. ' +
  'Approved/Pending/Rejected). Note = a detail distinct from Status. ' +
  'Newest first. If none, one short sentence.'

/**
 * Shared "send this prompt as the user" flow: ensures a session exists,
 * (optionally) appends the user bubble, then kicks off the streamed response.
 * Used by the suggestion chips and the header / sidebar quick-action buttons so
 * they don't each reimplement session + message bootstrapping.
 *
 * `showUserMessage: false` sends the prompt to the agent without rendering a
 * user bubble — only the assistant's reply appears in the thread.
 */
export const sendLabraChatPrompt =
  (
    partnerId: string,
    prompt: string,
    {
      showUserMessage = true,
      displayMessage,
      documents,
    }: {
      showUserMessage?: boolean
      displayMessage?: string
      documents?: ChatUploadedDocument[]
    } = {}
  ) =>
  (dispatch: LabraChatThunkDispatch, getState: () => LabraChatThunkState) => {
    const trimmed = prompt.trim()
    if (!trimmed || !partnerId) return

    const state = getState()
    let activeSessionId = state.labraChat?.sessionId
    const isNewConversation = !activeSessionId
    if (!activeSessionId) {
      activeSessionId = uuidv4()
      dispatch(setLabraChatSessionId(activeSessionId))
    }

    const turn = startChatTurn(activeSessionId)
    if (isNewConversation) {
      emitProductEvent({ name: 'labra_ai_chat_started', ...turn })
    }
    emitProductEvent({ name: 'labra_ai_chat_message_sent', ...turn })

    if (showUserMessage) {
      dispatch(
        addLabraChatMessage({
          id: uuidv4(),
          role: 'USER',
          // An explicit displayMessage wins (incl. '' for an upload-only turn,
          // which renders as pills with no text); otherwise show the prompt.
          content:
            displayMessage !== undefined ? displayMessage.trim() : trimmed,
          isStreaming: false,
          agentStage: null,
          stageMessage: null,
          completedStages: [],
          timestamp: new Date().toISOString(),
          documents: documents?.map(doc => ({
            filename: doc.filename,
            s3_uri: doc.s3_uri,
          })),
        })
      )
    }

    dispatch(
      sendLabraChatMessage(partnerId, {
        userId: state.userProfile?.userProfile?.id || '',
        sessionId: activeSessionId,
        turnId: turn.turn_id,
        message: trimmed,
        documents,
      })
    )
  }

/** Page size when reading a session's history (100 is the endpoint max — bigger
 * pages mean fewer round-trips and a far higher ceiling before the page guard). */
const HISTORY_PAGE_SIZE = 100
/** Safety cap so a misbehaving cursor can't loop forever (100 * 100 = 10k msgs). */
const MAX_HISTORY_PAGES = 100

/** Marker the backend prepends to a funding-document upload turn. */
const UPLOAD_PROMPT_MARKER = '[Funding user uploads]'

/** Pull the first balanced `{…}` JSON object out of a string, or null. */
const extractFirstJsonObject = (text: string): string | null => {
  const start = text.indexOf('{')
  if (start === -1) return null
  let depth = 0
  for (let i = start; i < text.length; i += 1) {
    if (text[i] === '{') depth += 1
    else if (text[i] === '}') {
      depth -= 1
      if (depth === 0) return text.slice(start, i + 1)
    }
  }
  return null
}

/**
 * Recover a funding upload's attachments from the machine prompt the backend
 * stores ("[Funding user uploads] {documents json} Pass documents verbatim …").
 * Returns the parsed documents (so the thread can render the same pills the live
 * bubble shows) or null when the message isn't an upload prompt.
 */
const parseUploadPromptDocuments = (
  content: string
): LabraChatMessageDocument[] | null => {
  if (!content.trimStart().startsWith(UPLOAD_PROMPT_MARKER)) return null
  const json = extractFirstJsonObject(content)
  if (!json) return []
  try {
    const parsed = JSON.parse(json) as {
      documents?: { filename?: string; s3_uri?: string }[]
    }
    return (parsed.documents ?? [])
      .filter(doc => doc.filename && doc.s3_uri)
      .map(doc => ({
        filename: doc.filename as string,
        s3_uri: doc.s3_uri as string,
      }))
  } catch {
    return []
  }
}

/** A stored history message → the shape the thread renders. Already final, so
 * no streaming/stage state. Roles are stored uppercase, matching our union.
 *
 * Upload turns are shown as attachment pills (filename + open link) alongside
 * any real message text. The backend returns the files in a structured
 * `attachments` array; turns stored before that change instead embedded a
 * "[Funding user uploads]" machine prompt, which we parse as a fallback and
 * never render as text. */
const toLabraChatMessage = (message: ChatHistoryMessage): LabraChatMessage => {
  const attachmentDocuments = (message.attachments ?? [])
    .filter(att => att.filename && att.s3_uri)
    .map(att => ({ filename: att.filename, s3_uri: att.s3_uri }))
  const legacyDocuments =
    !attachmentDocuments.length && message.role === 'USER'
      ? parseUploadPromptDocuments(message.content)
      : null
  const documents = attachmentDocuments.length
    ? attachmentDocuments
    : (legacyDocuments ?? [])
  const isLegacyUploadPrompt = message.content
    .trimStart()
    .startsWith(UPLOAD_PROMPT_MARKER)
  return {
    id: message.event_id,
    role: message.role,
    content: isLegacyUploadPrompt ? '' : message.content,
    documents: documents.length ? documents : undefined,
    isStreaming: false,
    agentStage: null,
    stageMessage: null,
    completedStages: [],
    timestamp: message.timestamp,
    streamSettled: true,
  }
}

/**
 * Walk every page of a session's history (cursor pagination) and return the
 * messages oldest-first. Each page is newest-first, so we reverse it and
 * prepend older pages above newer ones (see endpoint docs).
 */
const fetchAllHistoryPages = async (
  partnerId: string,
  userId: string,
  sessionId: string
): Promise<LabraChatMessage[]> => {
  const pages: LabraChatMessage[][] = []
  let nextToken: string | null = null
  const seenTokens = new Set<string>()
  let pageCount = 0
  do {
    const { data } = await getChatHistoryAPI(partnerId, {
      user_id: userId,
      session_id: sessionId,
      limit: HISTORY_PAGE_SIZE,
      next_token: nextToken,
    })
    pages.unshift(data.messages.map(toLabraChatMessage).reverse())
    pageCount += 1
    // Only continue while the server both says there's more AND hands back a
    // fresh cursor — a missing or repeated token means stop (no infinite loop,
    // no duplicated page).
    const token = data.has_more ? data.next_token : null
    nextToken = token && !seenTokens.has(token) ? token : null
    if (nextToken) seenTokens.add(nextToken)
  } while (nextToken && pageCount < MAX_HISTORY_PAGES)
  return pages.flat()
}

/**
 * Load a past conversation into the thread when the user picks it from history:
 * fetch all of its messages, abort any in-flight stream, and swap the thread +
 * on success so the caller can leave the history view (and stay put on failure,
 * where the caller can surface an inline error state).
 */
export const loadLabraChatSession =
  (partnerId: string, sessionId: string) =>
  async (
    dispatch: LabraChatThunkDispatch,
    getState: () => LabraChatThunkState
  ): Promise<boolean> => {
    const userId = getState().userProfile?.userProfile?.id || ''
    if (!partnerId || !sessionId || !userId) return false

    dispatch(startLoading(LABRA_CHAT_LOAD_SESSION))
    try {
      const messages = await fetchAllHistoryPages(partnerId, userId, sessionId)
      // Tear down any stream from the conversation we're leaving so its tokens
      // can't bleed into the one we just loaded.
      endChatTurn('navigated')
      cancelLabraChatSSE()
      dispatch(finalizeStreamingLabraChatMessages())
      dispatch(setLabraChatMessages(messages))
      dispatch(setLabraChatSessionId(sessionId))
      dispatch(setLabraChatNextBestActions([]))
      dispatch(setLabraChatGeneratingNextActions(false))
      return true
    } catch (error) {
      errorLogger({ globalState: getState() })(error as Error)
      return false
    } finally {
      dispatch(stopLoading(LABRA_CHAT_LOAD_SESSION))
    }
  }
